import { useId, type ReactNode } from 'react';

import { currencySymbol, formatMinor, parseAmountToMinor } from '../../shared/amount.js';
import { useLocale, useStrings } from '../i18n.js';

/**
 * The amount field.
 *
 * The one piece of typography that does real work: large, tabular figures, with the
 * currency symbol as a static prefix rather than something to type.
 *
 * Under the field, the parsed value is echoed back ("That's €1,200"). That line is what
 * resolves the genuinely ambiguous inputs, since "1.200" reads differently depending on
 * where you grew up: it shows the person what the product understood before they commit.
 */

interface AmountInputProps {
  label: string;
  hint?: string;
  currency: string;
  value: string;
  onChange: (value: string) => void;
  error?: string | undefined;
  autoFocus?: boolean;
  disabled?: boolean;
  /**
   * Optional control placed beside the field on wider screens — the create form puts
   * its submit button here, as the wireframe does, so the whole creation action stays
   * above the fold on a laptop.
   */
  trailing?: ReactNode;
}

export function AmountInput({
  label,
  hint,
  currency,
  value,
  onChange,
  error,
  autoFocus,
  disabled,
  trailing,
}: AmountInputProps) {
  const s = useStrings();
  const locale = useLocale();

  const id = useId();
  const hintId = `${id}-hint`;
  const echoId = `${id}-echo`;
  const errorId = `${id}-error`;

  const parsed = value.trim() === '' ? null : parseAmountToMinor(value, currency);
  const echo =
    parsed?.ok === true ? s.createForm.amountEcho(formatMinor(parsed.minor, currency, locale)) : '';

  const describedBy = [hint ? hintId : null, echo ? echoId : null, error ? errorId : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>

      <div className="amount-row">
        <div className="amount-input" data-invalid={error ? 'true' : 'false'}>
          <span className="amount-input__symbol" aria-hidden="true">
            {currencySymbol(currency, locale)}
          </span>
          <input
            id={id}
            className="amount-input__control"
            type="text"
            inputMode="decimal"
            autoComplete="off"
            enterKeyHint="done"
            placeholder="0"
            value={value}
            disabled={disabled}
            /*
             * Autofocus is normally a smell. Here the private room page is a single
             * question with one field and nothing else to read first, so moving the caret
             * into it saves a tap on mobile without stealing focus from any other content.
             * It is off for the create form, which has fields above it.
             */
            autoFocus={autoFocus}
            aria-describedby={describedBy || undefined}
            aria-invalid={error ? true : undefined}
            onChange={(event) => {
              onChange(event.target.value);
            }}
          />
          {/* The symbol is decorative above; the currency is named for screen readers. */}
          <span className="visually-hidden">{s.common.amountInCurrency(currency)}</span>
        </div>
        {trailing}
      </div>

      <span className="amount-echo" id={echoId} aria-live="polite">
        {echo}
      </span>

      {hint ? (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      ) : null}

      <span className="field__error" id={errorId} role="alert">
        {error ?? ''}
      </span>
    </div>
  );
}
